
import { supabase } from './supabase';
import { Drill, GameEvent, GameSuggestion, HudlTeamReport, VoiceParseResult } from "../types";

/** Returns Authorization header with a fresh Supabase session token, or throws. */
async function authHeader(): Promise<{ Authorization: string; 'Content-Type': string }> {
  let { data: { session } } = await supabase.auth.getSession();
  const now = Math.floor(Date.now() / 1000);
  const needsRefresh = !session
    || !session.expires_at                               // no expiry info — refresh to be safe
    || session.expires_at - now < 60;                   // expired or expiring within 60s
  if (needsRefresh) {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) console.warn('[authHeader] refresh failed:', error.message);
    if (data.session) session = data.session;
  }
  if (!session?.access_token) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
  };
}

export const generateDrills = async (
  focusArea: string,
  unitSystem: 'imperial' | 'metric' = 'imperial',
  skillLevel?: string
): Promise<Drill[]> => {
  try {
    const res = await fetch('/api/gemini/drills', {
      method: 'POST',
      headers: await authHeader(),
      body: JSON.stringify({ focusArea, unitSystem, skillLevel }),
    });
    return await res.json();
  } catch (error) {
    console.error("Error generating drills:", error);
    return [];
  }
};

export const extractScheduleFromFile = async (base64Input: string): Promise<Partial<GameEvent>[]> => {
  const res = await fetch('/api/gemini/extract-schedule', {
    method: 'POST',
    headers: await authHeader(),
    body: JSON.stringify({ base64Input }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}${body ? `: ${body}` : ''}`);
  }
  return await res.json();
};

export interface ExtractedStats {
  jerseyNumber: string;
  playerName?: string;
  setsPlayed?: number;       // GP / S — sets played in the match; drives K/G, ACE/G, etc.
  // Hitting
  kills: number;
  errors: number;
  attempts: number;
  hittingPct?: number;       // H% from sheet — validation display only
  // Serving
  aces: number;
  serveErrors: number;
  servingAttempts?: number;
  servingRating?: number;    // validation display only
  servingPct?: number;       // validation display only
  // Blocking
  blocks: number;            // block solo
  blockAssist?: number;
  blockTotal?: number;       // validation display only
  // Digs / Setting
  digs: number;
  assists: number;
  // Serve Receive
  srPerfect?: number;
  srGood?: number;
  srPoor?: number;
  srError?: number;
  srRating?: number;         // validation display only
}

// PREMIUM FEATURE: AI Practice Insights — will be gated behind a subscription paywall in a future release.
export interface PracticeInsight {
  skill: string;
  rationale: string;
  drills: Drill[];
}

export const generateAIPracticeInsights = async (
  statsSummary: string,
  unitSystem: 'imperial' | 'metric' = 'imperial',
  skillLevel?: string
): Promise<PracticeInsight[]> => {
  try {
    const res = await fetch('/api/gemini/ai-practice-insights', {
      method: 'POST',
      headers: await authHeader(),
      body: JSON.stringify({ statsSummary, unitSystem, skillLevel }),
    });
    return await res.json();
  } catch (error) {
    console.error("Error generating AI practice insights:", error);
    return [];
  }
};

export interface ParsedLineupSheet {
  I: string; II: string; III: string; IV: string; V: string; VI: string;
  libero: string;
}

export const parseLineupSheet = async (base64Input: string): Promise<ParsedLineupSheet> => {
  const res = await fetch('/api/gemini/parse-lineup-sheet', {
    method: 'POST',
    headers: await authHeader(),
    body: JSON.stringify({ base64Input }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}${body ? `: ${body}` : ''}`);
  }
  return res.json();
};

// PREMIUM FEATURE: In-game AI coaching suggestions — will be gated behind a subscription paywall.
export interface GameSuggestionsRequest {
  possession: 'serving' | 'receiving';
  ourScore: number;
  theirScore: number;
  set: number;
  currentRotation: number;
  server: {
    name: string;
    position: string;
    gameAces: number;
    gameErrors: number;
    gameAttempts: number;
    seasonAces: number;
    seasonErrors: number;
    seasonAttempts: number;
    serveTypeBreakdown: string;
    zoneBreakdown: string;
  };
  frontRow: {
    name: string;
    position: string;
    gameKills: number;
    gameErrors: number;
    gameAttempts: number;
    seasonKills: number;
    seasonErrors: number;
    seasonAttempts: number;
  }[];
  setter: {
    name: string;
    inFrontRow: boolean;
    gameAssists: number;
    seasonAssists: number;
  } | null;
  teamMetrics: {
    hitEfficiency: number;
    serveAceRate: number;
    serveErrorRate: number;
    sideoutPct: number;
    receptionAvg: number;
    totalStats: number;
  };
  opponentData: {
    roster: { name: string; position?: string; jerseyNumber?: string }[];
    passStats: { name: string; avgPassRating: number }[];
  } | null;
}

export const generateGameSuggestions = async (context: GameSuggestionsRequest): Promise<GameSuggestion[]> => {
  try {
    const res = await fetch('/api/gemini/game-suggestions', {
      method: 'POST',
      headers: await authHeader(),
      body: JSON.stringify(context),
    });
    const data = await res.json();
    return data.suggestions ?? [];
  } catch (error) {
    console.error('Error generating game suggestions:', error);
    return [];
  }
};

export interface ExtractStatsResult {
  players: ExtractedStats[];
  teamReport: HudlTeamReport | null;
  setScores?: Array<{ our: number; their: number }>;
}

export interface VoicePlayerContext {
  id: string;
  name: string;
  jerseyNumber: string;
  position?: string;
}

export interface VoiceGameContext {
  ourLineup: Record<string, VoicePlayerContext | null>;
  ourRoster: VoicePlayerContext[];
  opponentLineup: Record<string, VoicePlayerContext | null> | null;
  opponentRoster: VoicePlayerContext[] | null;
}

/**
 * Voice stat parsing. Send EITHER a recorded audio clip (base64, preferred — the
 * model transcribes with the roster in context) OR a plain-text transcript.
 */
export const parseVoiceTranscript = async (params: {
  audio?: { data: string; mimeType: string };
  transcript?: string;
  gameContext: VoiceGameContext;
  orgId: string;
}): Promise<VoiceParseResult> => {
  const res = await fetch('/api/gemini/voice-parse', {
    method: 'POST',
    headers: await authHeader(),
    body: JSON.stringify(params),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 429) throw new Error(err.error ?? 'Monthly AI budget reached');
    throw new Error(err.error ?? `Server error ${res.status}`);
  }
  return res.json();
};

export interface ExtractedRosterPlayer {
  name: string;
  jerseyNumber: string;
  position: string;
  heightCm: number | null;
  weightKg: number | null;
  handedness: string;
  gradYear: string;
}

export const extractRosterFromImage = async (base64Input: string): Promise<ExtractedRosterPlayer[]> => {
  const res = await fetch('/api/gemini/extract-roster', {
    method: 'POST',
    headers: await authHeader(),
    body: JSON.stringify({ base64Input }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API error ${res.status}${body ? `: ${body}` : ''}`);
  }
  return res.json();
};

export interface SetInsightsTip {
  category: string;
  suggestion: string;
  rationale: string;
}

export interface SetInsightsRequest {
  completedSet: number;
  setScore: { our: number; their: number };
  rotationSideout: Array<{ rotation: string; soAttempts: number; soWon: number; bpAttempts: number; bpWon: number }>;
  passerData: Array<{ name: string; position: string; handCount: number; handAvg: number; platformCount: number; platformAvg: number; floatCount: number; floatAvg: number; topspinCount: number; topspinAvg: number }>;
  serverData: Array<{ name: string; position: string; attempts: number; aces: number; errors: number; jumpAttempts: number; floatAttempts: number; jumpFloatAttempts: number }>;
  hitterData: Array<{ name: string; position: string; kills: number; errors: number; attempts: number }>;
  currentLineup: Array<{ slot: string; playerName: string; playerPosition: string }>;
}

export const generateSetInsights = async (params: SetInsightsRequest): Promise<SetInsightsTip[]> => {
  try {
    const res = await fetch('/api/gemini/set-insights', {
      method: 'POST',
      headers: await authHeader(),
      body: JSON.stringify(params),
    });
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error generating set insights:', error);
    return [];
  }
};

export const extractStatsFromImage = async (base64Input: string): Promise<ExtractStatsResult> => {
  const res = await fetch('/api/gemini/extract-stats', {
    method: 'POST',
    headers: await authHeader(),
    body: JSON.stringify({ base64Input }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `Server error ${res.status}`);
  }
  return res.json();
};
